import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * "Resend Passcode?" link with a tapper. After click, the button disables
 * itself and shows a countdown ("Resend in 12s"). When the countdown hits
 * zero it re-enables. Cooldown defaults to 15 seconds per the requirement.
 *
 * The underlying element is a <button type="button">, never an <a>, because
 * resending isn't a navigation and accessibility expects buttons for in-page
 * actions.
 *
 * Cooldown survives mid-cooldown unmount + remount via the optional
 * `cooldownUntilRef` prop — pass a ref that lives in the parent so a
 * back-then-forward navigation between steps doesn't reset the gate.
 */
type ResendCooldownButtonProps = {
  onResend: () => void | Promise<void>;
  cooldownSeconds?: number;
  /** When supplied, the cooldown end-time is mirrored into this ref so the
   *  component can restore the tapper after an unmount/remount. */
  cooldownUntilRef?: { current: number };
  disabled?: boolean;
};

export function ResendCooldownButton({
  onResend,
  cooldownSeconds = 15,
  cooldownUntilRef,
  disabled,
}: ResendCooldownButtonProps) {
  const [remaining, setRemaining] = useState<number>(() => {
    if (!cooldownUntilRef) return 0;
    const ms = cooldownUntilRef.current - Date.now();
    return ms > 0 ? Math.ceil(ms / 1000) : 0;
  });
  // Internal in-flight guard. The parent's `disabled` prop may not have
  // propagated yet on the synchronous click that fires onResend (state
  // updates are batched), so we also block re-entry locally for the window
  // between click and cooldown-start.
  const [submitting, setSubmitting] = useState(false);
  const tickRef = useRef<number | null>(null);

  const startTicking = useCallback(() => {
    if (tickRef.current !== null) return;
    tickRef.current = window.setInterval(() => {
      setRemaining((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          if (tickRef.current !== null) window.clearInterval(tickRef.current);
          tickRef.current = null;
          return 0;
        }
        return next;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    if (remaining > 0) startTicking();
    return () => {
      if (tickRef.current !== null) {
        window.clearInterval(tickRef.current);
        tickRef.current = null;
      }
    };
    // The mount-time initializer for `remaining` may already be > 0 when we
    // restore cooldown from a parent ref — start ticking in that case too.
  }, [remaining, startTicking]);

  const handleClick = useCallback(async () => {
    if (remaining > 0 || disabled || submitting) return;
    setSubmitting(true);
    try {
      await onResend();
      setRemaining(cooldownSeconds);
      if (cooldownUntilRef) cooldownUntilRef.current = Date.now() + cooldownSeconds * 1000;
      startTicking();
    } finally {
      setSubmitting(false);
    }
  }, [remaining, disabled, submitting, onResend, cooldownSeconds, cooldownUntilRef, startTicking]);

  const isCoolingDown = remaining > 0;
  const isDisabled = isCoolingDown || disabled || submitting;
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      aria-busy={submitting || undefined}
      className={[
        "font-['Michroma','Orbitron',sans-serif] text-[11px] uppercase tracking-[1.1px] underline-offset-4",
        isDisabled
          ? 'cursor-not-allowed text-white/40 no-underline'
          : 'text-[#00ff8b] underline hover:text-[#7afcc6]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#3a2f44]',
      ].join(' ')}
      aria-live="polite"
    >
      {submitting ? 'Sending…' : isCoolingDown ? `Resend in ${remaining}s` : 'Resend Passcode?'}
    </button>
  );
}
